import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import { Loader2, Sparkles, Upload, Trash2, Wand2, Save, ArrowLeft, Image as ImageIcon, RefreshCw, GripVertical, Send, ExternalLink } from "lucide-react";

type PostStatus = "draft" | "scheduled" | "published";
type GalleryImg = {
  id: string; url: string; alt: string | null; caption: string | null;
  position: number; width: number | null; height: number | null;
};
type SocialRow = {
  id: string; channel: string; status: string; copy: string | null;
  scheduled_at: string | null; published_at: string | null; remote_url: string | null; error: string | null;
};

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const CHANNELS: { key: string; label: string }[] = [
  { key: "pinterest", label: "Pinterest" },
  { key: "twitter", label: "X / Twitter" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube_shorts", label: "YouTube Shorts" },
  { key: "instagram_reels", label: "Instagram Reels" },
];

const SITE = "https://bookmystudiospace.com";

export default function AdminBlogComposer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [postId, setPostId] = useState<string | null>(isNew ? null : id!);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [status, setStatus] = useState<PostStatus>("draft");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagsInput, setTagsInput] = useState("");

  const [gallery, setGallery] = useState<GalleryImg[]>([]);
  const [social, setSocial] = useState<SocialRow[]>([]);

  // AI Studio state
  const [aiBusy, setAiBusy] = useState<string | null>(null);
  const [outlinePrompt, setOutlinePrompt] = useState("");
  const [outlineKeywords, setOutlineKeywords] = useState("");
  const [outlineResult, setOutlineResult] = useState<string>("");
  const [rewriteInstr, setRewriteInstr] = useState("Tighten and make it more scannable");

  const load = async (pid: string) => {
    setLoading(true);
    const [{ data: post }, { data: g }, { data: s }] = await Promise.all([
      supabase.from("blog_posts").select("*").eq("id", pid).maybeSingle(),
      supabase.from("blog_gallery_images").select("*").eq("post_id", pid).order("position"),
      supabase.from("blog_social_posts").select("*").eq("post_id", pid).order("channel"),
    ]);
    setLoading(false);
    if (!post) { toast.error("Post not found"); navigate("/admin/blog"); return; }
    setTitle(post.title); setSlug(post.slug); setExcerpt(post.excerpt ?? "");
    setBody(post.body ?? ""); setCoverImage(post.cover_image ?? "");
    setStatus(post.status as PostStatus); setSeoTitle(post.seo_title ?? "");
    setSeoDescription(post.seo_description ?? ""); setTags(post.tags ?? []);
    setGallery((g as any) ?? []); setSocial((s as any) ?? []);
  };

  useEffect(() => { if (!isNew) load(id!); }, [id]);

  const save = async () => {
    if (!title.trim()) return toast.error("Title required");
    const finalSlug = slug.trim() || slugify(title);
    setSaving(true);
    const payload = {
      title: title.trim(), slug: finalSlug,
      excerpt: excerpt || null, body: body || null,
      cover_image: coverImage || null, status, tags,
      seo_title: seoTitle || null, seo_description: seoDescription || null,
      publish_at: status === "published" ? new Date().toISOString() : null,
    };
    let savedId = postId;
    if (postId) {
      const { error } = await supabase.from("blog_posts").update(payload).eq("id", postId);
      if (error) { setSaving(false); return toast.error(error.message); }
    } else {
      const { data, error } = await supabase.from("blog_posts").insert(payload).select("id").maybeSingle();
      if (error || !data) { setSaving(false); return toast.error(error?.message ?? "Insert failed"); }
      savedId = data.id; setPostId(data.id);
      navigate(`/admin/blog/${data.id}`, { replace: true });
    }
    setSaving(false);
    toast.success("Saved");
    // fire publish hook on first-time publish
    if (savedId && status === "published") {
      supabase.functions.invoke("blog-publish-hook", { body: { post_id: savedId } })
        .then(({ error }) => { if (error) toast.error(`Publish hook: ${error.message}`); });
    }
  };

  // ---------- Gallery ----------
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const ensurePostId = async (): Promise<string | null> => {
    if (postId) return postId;
    if (!title.trim()) { toast.error("Enter a title first (post must exist before adding images)"); return null; }
    const finalSlug = slug.trim() || slugify(title);
    const { data, error } = await supabase.from("blog_posts").insert({
      title: title.trim(), slug: finalSlug, status: "draft",
    }).select("id").maybeSingle();
    if (error || !data) { toast.error(error?.message ?? "Could not create post"); return null; }
    setPostId(data.id);
    navigate(`/admin/blog/${data.id}`, { replace: true });
    return data.id;
  };

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const pid = await ensurePostId();
    if (!pid) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop() ?? "jpg";
      const path = `${pid}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("blog").upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) { toast.error(`Upload failed: ${upErr.message}`); continue; }
      const { data: pub } = supabase.storage.from("blog").getPublicUrl(path);
      const url = pub.publicUrl;

      // auto alt
      let alt: string | null = null;
      try {
        const { data, error } = await supabase.functions.invoke("blog-ai-assist", {
          body: { action: "alt", image_url: url, context: title, post_id: pid },
        });
        if (!error) alt = (data as any)?.output?.alt ?? null;
      } catch { /* ignore */ }

      const nextPos = (gallery[gallery.length - 1]?.position ?? -1) + 1;
      const { data: row, error: insErr } = await supabase.from("blog_gallery_images")
        .insert({ post_id: pid, url, alt, position: nextPos }).select("*").maybeSingle();
      if (insErr) { toast.error(insErr.message); continue; }
      setGallery(prev => [...prev, row as any]);
    }
    setUploading(false);
    toast.success("Images uploaded");
  };

  const removeImage = async (imgId: string) => {
    if (!confirm("Remove this image from the gallery?")) return;
    const { error } = await supabase.from("blog_gallery_images").delete().eq("id", imgId);
    if (error) return toast.error(error.message);
    setGallery(prev => prev.filter(g => g.id !== imgId));
  };

  const regenAlt = async (img: GalleryImg) => {
    setAiBusy(`alt-${img.id}`);
    try {
      const { data, error } = await supabase.functions.invoke("blog-ai-assist", {
        body: { action: "alt", image_url: img.url, context: title, post_id: postId },
      });
      if (error) throw error;
      const alt = (data as any)?.output?.alt ?? "";
      await supabase.from("blog_gallery_images").update({ alt }).eq("id", img.id);
      setGallery(prev => prev.map(g => g.id === img.id ? { ...g, alt } : g));
    } catch (e: any) { toast.error(e.message); }
    finally { setAiBusy(null); }
  };

  const updateAlt = async (img: GalleryImg, alt: string) => {
    setGallery(prev => prev.map(g => g.id === img.id ? { ...g, alt } : g));
    await supabase.from("blog_gallery_images").update({ alt }).eq("id", img.id);
  };

  // ---------- AI actions ----------
  const callAI = async (action: string, params: any, busyKey: string): Promise<any | null> => {
    setAiBusy(busyKey);
    try {
      const { data, error } = await supabase.functions.invoke("blog-ai-assist", {
        body: { action, post_id: postId, ...params },
      });
      if (error) throw error;
      return (data as any)?.output ?? null;
    } catch (e: any) { toast.error(`AI ${action} failed: ${e.message}`); return null; }
    finally { setAiBusy(null); }
  };

  const genOutline = async () => {
    if (!outlinePrompt.trim()) return toast.error("Enter a topic");
    const out = await callAI("outline", { topic: outlinePrompt, keywords: outlineKeywords }, "outline");
    if (!out) return;
    const md = (out.outline ?? []).map((s: any) =>
      `## ${s.heading}\n${(s.points ?? []).map((p: string) => `- ${p}`).join("\n")}`
    ).join("\n\n");
    setOutlineResult(md || out.raw || "");
  };

  const genDraft = async () => {
    if (!title.trim()) return toast.error("Enter a title");
    const out = await callAI("draft", { title, outline: outlineResult, keywords: outlineKeywords }, "draft");
    if (out?.body) setBody(out.body);
  };

  const genSeo = async () => {
    if (!body) return toast.error("Write the body first");
    const out = await callAI("seo", { title, body }, "seo");
    if (out?.seo_title) setSeoTitle(out.seo_title);
    if (out?.seo_description) setSeoDescription(out.seo_description);
    toast.success("SEO updated");
  };

  const genExcerpt = async () => {
    if (!body) return toast.error("Write the body first");
    const out = await callAI("excerpt", { title, body }, "excerpt");
    if (out?.excerpt) setExcerpt(out.excerpt);
  };

  const genTags = async () => {
    if (!body) return toast.error("Write the body first");
    const out = await callAI("tags", { title, body, existingTags: tags }, "tags");
    if (out?.tags?.length) { setTags(out.tags); toast.success("Tags updated"); }
  };

  const rewriteBody = async () => {
    if (!body) return toast.error("Nothing to rewrite");
    const out = await callAI("rewrite", { text: body, instruction: rewriteInstr }, "rewrite");
    if (out?.text) setBody(out.text);
  };

  // ---------- Social copy ----------
  const ensureSocial = async (channel: string): Promise<SocialRow | null> => {
    const existing = social.find(s => s.channel === channel);
    if (existing) return existing;
    const pid = await ensurePostId(); if (!pid) return null;
    const { data, error } = await supabase.from("blog_social_posts")
      .insert({ post_id: pid, channel, status: "draft" } as any).select("*").maybeSingle();
    if (error || !data) { toast.error(error?.message ?? "Could not create social row"); return null; }
    setSocial(prev => [...prev, data as any]);
    return data as any;
  };

  const genSocial = async (channel: string) => {
    const url = `${SITE}/blog/${slug || slugify(title)}`;
    const out = await callAI("social_copy", { channel, title, excerpt: excerpt || title, url }, `social-${channel}`);
    if (!out) return;
    const copy = typeof out.text === "string" ? out.text
      : out.tweets ? out.tweets.join("\n\n---\n\n")
      : out.title && out.description ? `${out.title}\n\n${out.description}`
      : JSON.stringify(out, null, 2);
    const row = await ensureSocial(channel);
    if (!row) return;
    await supabase.from("blog_social_posts").update({ copy }).eq("id", row.id);
    setSocial(prev => prev.map(s => s.id === row.id ? { ...s, copy } : s));
  };

  const updateSocialCopy = async (row: SocialRow, copy: string) => {
    setSocial(prev => prev.map(s => s.id === row.id ? { ...s, copy } : s));
    await supabase.from("blog_social_posts").update({ copy }).eq("id", row.id);
  };

  const publishChannel = async (channel: string) => {
    if (status !== "published") { toast.error("Save the post as Published first."); return; }
    const row = await ensureSocial(channel);
    if (!row) return;
    setAiBusy(`pub-${channel}`);
    const { data, error } = await supabase.functions.invoke("blog-social-publish", { body: { social_post_id: row.id } });
    setAiBusy(null);
    if (error) { toast.error(`Publish failed: ${error.message}`); }
    else if (data?.ok === false) { toast.error(`Publish failed: ${data.error}`); }
    else { toast.success(`Published to ${channel}`); }
    // refresh row
    const { data: fresh } = await supabase.from("blog_social_posts").select("*").eq("id", row.id).maybeSingle();
    if (fresh) setSocial(prev => prev.map(s => s.id === row.id ? (fresh as any) : s));
  };

  const commitTags = () => {
    const arr = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
    if (arr.length) setTags(prev => Array.from(new Set([...prev, ...arr])));
    setTagsInput("");
  };

  if (loading) return (
    <AdminShell title="BLOG COMPOSER">
      <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
    </AdminShell>
  );

  return (
    <AdminShell title={isNew && !postId ? "NEW POST" : "EDIT POST"}>
      <div className="flex items-center justify-between mb-6 gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin/blog")} className="font-body">
          <ArrowLeft className="w-4 h-4 mr-1" /> All posts
        </Button>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={(v) => setStatus(v as PostStatus)}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* LEFT — editor */}
        <div className="space-y-6">
          <div className="bg-panel border border-border p-4 space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Title</Label>
              <Input value={title} onChange={e => { setTitle(e.target.value); if (!slug) setSlug(slugify(e.target.value)); }} className="text-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Slug</Label>
                <Input value={slug} onChange={e => setSlug(slugify(e.target.value))} />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Cover image URL</Label>
                <Input value={coverImage} onChange={e => setCoverImage(e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="font-mono text-xs uppercase tracking-widest">Excerpt</Label>
                <Button size="sm" variant="outline" onClick={genExcerpt} disabled={aiBusy === "excerpt"} className="text-xs">
                  {aiBusy === "excerpt" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  AI excerpt
                </Button>
              </div>
              <Textarea rows={2} value={excerpt} onChange={e => setExcerpt(e.target.value)} />
            </div>
          </div>

          <Tabs defaultValue="body">
            <TabsList>
              <TabsTrigger value="body">Body</TabsTrigger>
              <TabsTrigger value="gallery">Gallery ({gallery.length})</TabsTrigger>
              <TabsTrigger value="seo">SEO & tags</TabsTrigger>
              <TabsTrigger value="social">Social</TabsTrigger>
            </TabsList>

            <TabsContent value="body" className="mt-4">
              <div className="bg-panel border border-border p-4">
                <RichTextEditor value={body} onChange={setBody} />
              </div>
            </TabsContent>

            <TabsContent value="gallery" className="mt-4">
              <div className="bg-panel border border-border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">
                    Alt text is auto-generated by AI on upload. Edit anytime.
                  </p>
                  <div>
                    <input ref={fileInputRef} type="file" accept="image/*" multiple hidden
                      onChange={e => { uploadFiles(e.target.files); e.target.value = ""; }} />
                    <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      className="bg-primary text-primary-foreground hover:bg-primary/90">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Upload images
                    </Button>
                  </div>
                </div>
                {gallery.length === 0 ? (
                  <p className="text-muted-foreground font-body text-sm py-6 text-center">No images yet. Upload some to build a gallery.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {gallery.map(img => (
                      <div key={img.id} className="border border-border bg-background p-3 flex gap-3">
                        <img src={img.url} alt={img.alt ?? ""} className="w-24 h-24 object-cover shrink-0" />
                        <div className="flex-1 min-w-0 space-y-2">
                          <Textarea
                            rows={2}
                            value={img.alt ?? ""}
                            onChange={e => setGallery(prev => prev.map(g => g.id === img.id ? { ...g, alt: e.target.value } : g))}
                            onBlur={e => updateAlt(img, e.target.value)}
                            placeholder="Alt text (for accessibility & SEO)"
                            className="text-xs"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => regenAlt(img)}
                              disabled={aiBusy === `alt-${img.id}`} className="text-xs">
                              {aiBusy === `alt-${img.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                              Regen alt
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => removeImage(img.id)}
                              className="text-xs text-destructive hover:text-destructive">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="seo" className="mt-4">
              <div className="bg-panel border border-border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="font-mono text-xs uppercase tracking-widest">SEO</Label>
                  <Button size="sm" variant="outline" onClick={genSeo} disabled={aiBusy === "seo"} className="text-xs">
                    {aiBusy === "seo" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    AI generate
                  </Button>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">SEO title</Label>
                  <Input value={seoTitle} onChange={e => setSeoTitle(e.target.value)} maxLength={70} />
                  <p className="text-xs text-muted-foreground font-mono mt-1">{seoTitle.length}/60</p>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">SEO description</Label>
                  <Textarea rows={3} value={seoDescription} onChange={e => setSeoDescription(e.target.value)} maxLength={170} />
                  <p className="text-xs text-muted-foreground font-mono mt-1">{seoDescription.length}/155</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="font-mono text-xs uppercase tracking-widest">Tags</Label>
                    <Button size="sm" variant="outline" onClick={genTags} disabled={aiBusy === "tags"} className="text-xs">
                      {aiBusy === "tags" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Suggest tags
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 font-mono">
                        {t}
                        <button onClick={() => setTags(prev => prev.filter(x => x !== t))} aria-label={`Remove tag ${t}`}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitTags(); } }}
                      placeholder="Add tag(s), comma-separated" className="text-sm" />
                    <Button size="sm" variant="outline" onClick={commitTags}>Add</Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="social" className="mt-4">
              <div className="bg-panel border border-border p-4 space-y-4">
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">
                  Generate per-channel copy, then publish. Pinterest, LinkedIn, and X/Twitter are live now — other channels arrive in Phase 4.
                </p>
                {CHANNELS.map(({ key, label }) => {
                  const row = social.find(s => s.channel === key);
                  const livePublish = key === "pinterest" || key === "linkedin" || key === "twitter";
                  return (
                    <div key={key} className="border border-border p-3 bg-background">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-display tracking-widest text-sm">{label}</span>
                          {row && <span className="text-[10px] font-mono uppercase px-2 py-0.5 bg-muted">{row.status}</span>}
                          {row?.remote_url && (
                            <a href={row.remote_url} target="_blank" rel="noreferrer"
                              className="text-[10px] font-mono uppercase text-primary flex items-center gap-1 hover:underline">
                              view <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => genSocial(key)}
                            disabled={aiBusy === `social-${key}`} className="text-xs">
                            {aiBusy === `social-${key}` ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                            {row?.copy ? "Regenerate" : "Generate"}
                          </Button>
                          {livePublish && (
                            <Button size="sm" onClick={() => publishChannel(key)}
                              disabled={aiBusy === `pub-${key}` || status !== "published"}
                              className="text-xs bg-primary text-primary-foreground hover:bg-primary/90">
                              {aiBusy === `pub-${key}` ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                              {row?.status === "published" ? "Republish" : "Publish now"}
                            </Button>
                          )}
                        </div>
                      </div>
                      <Textarea
                        rows={4}
                        value={row?.copy ?? ""}
                        placeholder={`AI-generated ${label} copy will appear here`}
                        onChange={async e => {
                          const r = row ?? await ensureSocial(key);
                          if (r) updateSocialCopy(r, e.target.value);
                        }}
                        className="text-xs font-mono"
                      />
                      {row?.error && (
                        <p className="text-[11px] font-mono text-destructive mt-2 break-words">Error: {row.error}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT — AI Studio */}
        <aside className="space-y-4">
          <div className="bg-panel border border-border p-4 space-y-3 sticky top-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="font-display tracking-widest text-sm">AI STUDIO</h3>
            </div>

            <details open className="border border-border">
              <summary className="p-2 cursor-pointer font-mono text-xs uppercase tracking-widest bg-background">Outline & draft</summary>
              <div className="p-3 space-y-2">
                <Input placeholder="Topic (e.g. cyclorama lighting)" value={outlinePrompt} onChange={e => setOutlinePrompt(e.target.value)} className="text-sm" />
                <Input placeholder="Target keywords (optional)" value={outlineKeywords} onChange={e => setOutlineKeywords(e.target.value)} className="text-sm" />
                <Button size="sm" onClick={genOutline} disabled={aiBusy === "outline"} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {aiBusy === "outline" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />}
                  Generate outline
                </Button>
                {outlineResult && (
                  <Textarea rows={6} value={outlineResult} onChange={e => setOutlineResult(e.target.value)} className="text-xs font-mono" />
                )}
                <Button size="sm" variant="outline" onClick={genDraft} disabled={aiBusy === "draft"} className="w-full">
                  {aiBusy === "draft" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Draft full post from outline
                </Button>
              </div>
            </details>

            <details className="border border-border">
              <summary className="p-2 cursor-pointer font-mono text-xs uppercase tracking-widest bg-background">Rewrite body</summary>
              <div className="p-3 space-y-2">
                <Textarea rows={2} value={rewriteInstr} onChange={e => setRewriteInstr(e.target.value)} className="text-xs" />
                <Button size="sm" onClick={rewriteBody} disabled={aiBusy === "rewrite"} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {aiBusy === "rewrite" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Rewrite body
                </Button>
              </div>
            </details>

            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground pt-2 border-t border-border">
              Powered by Lovable AI · Gemini 3.6 Flash
            </div>
          </div>
        </aside>
      </div>
    </AdminShell>
  );
}